/**
 * collectionPersistence.js — Collection persistence helpers
 *
 * Canonical field: `collectionKey`. `resolveCollectionKey` still reads legacy
 * `databaseKey` on existing docs. Writers set `collectionKey` only.
 *
 * Live root: `_collections`.
 */

function cleanKey(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Resolve the collection id from a doc, payload, or mixed object.
 * Prefers collectionKey, falls back to databaseKey / key (legacy).
 *
 * @param {object|null|undefined} source
 * @returns {string}
 */
export function resolveCollectionKey(source) {
  if (!source || typeof source !== 'object') return '';
  return cleanKey(source.collectionKey) || cleanKey(source.databaseKey) || cleanKey(source.key);
}

/**
 * Build a collection-key patch (`collectionKey` only).
 *
 * @param {string} collectionKey
 * @returns {{ collectionKey: string }|null}
 */
export function buildCollectionKeyFields(collectionKey) {
  const key = cleanKey(collectionKey);
  if (!key) return null;
  return { collectionKey: key };
}

/**
 * Merge collection key onto a data object (shallow copy).
 * Strips legacy `databaseKey` if present.
 *
 * @param {object} data
 * @param {string} [explicitCollectionKey]
 * @returns {object}
 */
export function withCollectionKey(data = {}, explicitCollectionKey = '') {
  const key = cleanKey(explicitCollectionKey) || resolveCollectionKey(data);
  const fields = buildCollectionKeyFields(key);
  if (!fields) {
    const next = { ...data };
    delete next.databaseKey;
    return next;
  }
  const next = {
    ...data,
    ...fields,
  };
  delete next.databaseKey;
  return next;
}
